import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import ActionDropdown from "./ActionDropdown";
import SortDropdown from "./SortDropdown";
import FlightLIsting from "./FlightLIsting";
import YourRequestFilter from "./YourRequestFilter";
import Pagination from "../../components/Pagination";
import { useLoadRequests } from "./useLoadRequests";

interface YourRequestProps {
  onShowHistory: () => void;
}

interface FilterState {
  status?: string;
}

const YourRequest: React.FC<YourRequestProps> = ({ onShowHistory }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [targetRequestId, setTargetRequestId] = useState<string | null>(() => {
    return (
      (location.state as any)?.targetRequestId ||
      (location.state as any)?.flightId ||
      searchParams.get("requestId")
    );
  });

  // Re-reads on every location.state change, not just at mount, so clicking a
  // second notification while already on this page (no remount happens since
  // it's the same route) still updates targetRequestId and re-triggers the
  // highlight below instead of being stuck with whatever was captured first.
  useEffect(() => {
    const incoming =
      (location.state as any)?.targetRequestId ||
      (location.state as any)?.flightId ||
      searchParams.get("requestId");
    if (incoming) {
      setTargetRequestId(incoming);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate, searchParams]);

  const {
    flightsDetails,
    loading,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    setStatus,
    currentPage,
    setCurrentPage,
    pageSize,
    fetchLoadRequests,
    handlePageChange,
    handlePageSizeChange,
  } = useLoadRequests(targetRequestId);

  // UI-only state (toolbar dropdowns + the in-flight bulk action).
  const [actionIsOpen, setActionIsOpen] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  // Pin state of the current selection (reported up from FlightLIsting) so the
  // Actions menu can show "Pin" vs "Unpin". null = nothing selected.
  const [selectedPinned, setSelectedPinned] = useState<boolean | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<FilterState>({
    status: "",
  });

  const filterButtonRef = useRef<HTMLButtonElement | null>(null);
  const [filterPosition, setFilterPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    if (!showFilter) {
      setFilterPosition(null);
      return;
    }
    const updatePosition = () => {
      const btn = filterButtonRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const EDGE_MARGIN = 16;
      const isMobile = window.innerWidth < 640;
      const modalWidth = isMobile
        ? Math.min(window.innerWidth - EDGE_MARGIN * 2, 320)
        : 320;

      const idealLeft = isMobile ? rect.left : rect.left;
      const maxLeft = window.innerWidth - modalWidth - EDGE_MARGIN;
      const left = Math.min(Math.max(idealLeft, EDGE_MARGIN), Math.max(maxLeft, EDGE_MARGIN));

      setFilterPosition({
        top: rect.bottom + 8,
        left,
        width: modalWidth,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [showFilter]);

  const handleFilterChange = (filters: FilterState) => {
    setStatus(filters.status || "");
    setSelectedFilters(filters);
    setShowFilter(false);
    // Reset to the first page on any status change so we don't land on a now
    // out-of-range page (e.g. switching to a filter with fewer results).
    setCurrentPage(1);
  };

  const handleActionClick = (action: string) => {
    setSelectedAction(action);
    // Reset the action after processing so the same action can fire again.
    setTimeout(() => setSelectedAction(null), 100);
  };

  const toggleFilter = () => setShowFilter((prev) => !prev);

  return (
    <>
      <div className="bg-white rounded-xl p-3 sm:p-6">
        <section className="flex flex-wrap gap-2 sm:gap-3 items-center justify-between mb-4">
          <p className="text-[#1F2937] text-lg sm:text-xl font-bold font-manrope w-full sm:w-auto">
            Your Requests
          </p>
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:ml-auto sm:justify-end">
            {/* Refresh Button */}
            <button
              onClick={() => fetchLoadRequests()}
              disabled={loading}
              className="flex items-center gap-2 p-2.5 sm:p-3 bg-white border border-[#EDEEF1] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-60"
              title="Refresh"
              aria-label="Refresh"
            >
              <i
                className={`st-reload text-[#000000] ${loading ? "animate-spin" : ""}`}
              ></i>
            </button>
            {/* Filters Button — first in the toolbar on phones */}
            <div className="relative order-first sm:order-none">
              <button
                ref={filterButtonRef}
                onClick={toggleFilter}
                className="flex items-center gap-2 p-2.5 sm:p-3 bg-white border border-[#EDEEF1] rounded-lg hover:bg-[#F9FAFB] transition-colors"
              >
                <i className="st-filter text-[#000000]"></i>
                <p className="font-medium text-[#000000] text-sm">Filters</p>
              </button>
              {showFilter && (
                <div
                  style={
                    filterPosition
                      ? { top: filterPosition.top, left: filterPosition.left, width: filterPosition.width }
                      : { visibility: "hidden" }
                  }
                  className="fixed z-50"
                >
                  <YourRequestFilter
                    activeFilters={selectedFilters}
                    onClose={() => setShowFilter(false)}
                    onFilterChange={handleFilterChange}
                  />
                </div>
              )}
            </div>
            <button
              onClick={onShowHistory}
              className="flex items-center gap-2 p-2.5 sm:p-3 bg-white border border-[#EDEEF1] rounded-lg hover:bg-[#F9FAFB] transition-colors"
            >
              <i className="st-previous text-[#000000]"></i>
              <p className="font-medium text-[#000000] text-sm">History</p>
            </button>
            <div className="relative inline-block">
              <button
                id="dropdownDefaultButton"
                onClick={() => setActionIsOpen((prev) => !prev)}
                disabled={selectedPinned === null}
                className="flex items-center gap-2 p-2.5 sm:p-3 bg-white border border-[#EDEEF1] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                type="button"
              >
                <i className="st-more text-[#000000] text-xs"></i>
                <p className="font-medium text-[#000000] text-sm">Actions</p>
              </button>
              <ActionDropdown
                actionIsOpen={actionIsOpen}
                setActionIsOpen={setActionIsOpen}
                onActionClick={handleActionClick}
                isPinned={selectedPinned}
              />
            </div>
            <div className="relative">
              <button
                className="flex items-center gap-2 p-2.5 sm:p-4 bg-white border border-[#EDEEF1] rounded-lg hover:bg-[#F9FAFB] transition-colors"
                type="button"
                onClick={() => setIsSortDropdownOpen((prev) => !prev)}
              >
                <i className="st-options text-[#000000] text-xs"></i>
              </button>

              <SortDropdown
                isOpen={isSortDropdownOpen}
                setIsOpen={setIsSortDropdownOpen}
                setSortBy={setSortBy}
                setSortOrder={setSortOrder}
                sortBy={sortBy}
                sortOrder={sortOrder}
              />
            </div>
          </div>
        </section>
        <FlightLIsting
          flights={flightsDetails}
          action={selectedAction}
          fetchLoadRequests={fetchLoadRequests}
          loading={loading}
          onSelectionChange={setSelectedPinned}
          selectedRequestId={targetRequestId}
        />
        {flightsDetails.totalCount > 0 && (
          <section>
            <Pagination
              currentPage={currentPage}
              totalCount={flightsDetails.totalCount}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              showTotal
              containerClassName="flex flex-row flex-wrap items-center justify-between gap-3 mt-4 sm:mt-6 text-sm"
            />
          </section>
        )}
      </div>
    </>
  );
};

export default YourRequest;
